import {BaseService} from "./BaseService";
import {AxiosResponse} from "axios";
import ApiResponse from "../Models/ApiResponse";
import api from "../Interceptors/Interceptor";
import ReferenceModel from "../Models/ReferenceModel";
import {Page} from "../Models/Page";
import {IDataserviceFieldModel, IDataserviceItemModel} from "./DataserviceService";

export type {IDataserviceFieldModel} from "./DataserviceService";

export interface IDatasourceItemModel {
	uuid: string
	dataservice: IDataserviceItemModel
	authenticationType?: IAuthenticationTypeModel
	datasourceFields: IDatasourceFieldModel[]
	name: string
	scheduleUuid?: string
	bigqueryDatasetPostfix?: string
	bigqueryDatasetName?: string
	connectionVerified?: boolean
	/** Present on list + GET /datasource/{uuid}; same payload as /connection/status. */
	connectionStatus?: IDatasourceOAuthStatus | null
	connectorStatus?: string
	connectorNextRunTs?: string
	connectorLastRanAtTs?: string
	connectorAttempts?: number
	ingestBlockReasons?: string[]
	active: boolean
	deleted: boolean
	customer: ReferenceModel
}

export interface IDatasourceFieldModel {
	uuid: string
	authenticationTypeField?: IAuthenticationTypeFieldModel
	dataserviceField?: IDataserviceFieldModel
	value: string | null
	secretStored?: boolean
	active: boolean
	deleted: boolean
	customer: ReferenceModel
}

export interface IAuthenticationTypeModel {
	uuid: string
	authenticationTypeFields: IAuthenticationTypeFieldModel[]
	code: string
	name: string
	active: boolean
	deleted: boolean
}

export interface IAuthenticationTypeFieldModel {
	uuid: string
	authenticationTypeUuid?: string
	sequence: number
	name: string
	label: string
	secret: boolean
	required: boolean
	internal?: boolean
	systemManaged?: boolean
	defaultValue?: string
	secretStored?: boolean
	fieldRole?: string
	active: boolean
	deleted: boolean
}

export interface IDatasourceOAuthStatus {
	oauth: boolean
	connected: boolean
	previouslyConnected?: boolean
	accessTokenExpired?: boolean
	tokenExpired?: boolean
	tokenRefreshFailed?: boolean
	expiresWithinHours?: number | null
	expiresWithinMinutes?: number | null
	tokenExpiration?: string | null
	refreshTokenExpired?: boolean
	refreshExpiresWithinDays?: number | null
	refreshExpiresWithinHours?: number | null
	refreshExpiresWithinMinutes?: number | null
	refreshTokenExpiration?: string | null
	healthCheckSupported?: boolean
	connectionTestSupported?: boolean
	connectionManaged?: boolean
	tokenRefreshSupported?: boolean
}

export interface IDatasourceEventModel {
	uuid: string
	datasourceId: string
	eventType: string
	contentTypeIcon?: string
	contentTypeDescription?: string
	payload?: Record<string, unknown>
	actorName?: string
	createdBy?: string
	createdAt?: string
}

export interface IDatasourceFieldOptionModel {
	value: string
	label: string
}

export const AUTH_TYPE_FIELD_ROLE = {
	OAUTH_AUTHORIZATION_URL: "OAUTH_AUTHORIZATION_URL",
	OAUTH_TOKEN_URL: "OAUTH_TOKEN_URL",
	OAUTH_GRANT_TYPE: "OAUTH_GRANT_TYPE",
	OAUTH_TOKEN_CLIENT_AUTH: "OAUTH_TOKEN_CLIENT_AUTH",
	OAUTH_CLIENT_ID: "OAUTH_CLIENT_ID",
	OAUTH_CLIENT_SECRET: "OAUTH_CLIENT_SECRET",
	OAUTH_CALLBACK_HMAC: "OAUTH_CALLBACK_HMAC",
	OAUTH_PKCE: "OAUTH_PKCE",
	OAUTH_TOKEN_RESPONSE_PATH: "OAUTH_TOKEN_RESPONSE_PATH",
	OAUTH_TOKEN_FAIL_PATH: "OAUTH_TOKEN_FAIL_PATH",
	OAUTH_TOKEN_FAIL_VALUE: "OAUTH_TOKEN_FAIL_VALUE",
	OAUTH_TOKEN_RESPONSE_FIELD: "OAUTH_TOKEN_RESPONSE_FIELD",
	OAUTH_TOKEN_BODY_TYPE: "OAUTH_TOKEN_BODY_TYPE",
	OAUTH_TOKEN_CODE_PARAM: "OAUTH_TOKEN_CODE_PARAM",
	OAUTH_CLIENT_ID_PARAM: "OAUTH_CLIENT_ID_PARAM",
	OAUTH_SCOPE: "OAUTH_SCOPE",
	OAUTH_ACCESS_TOKEN: "OAUTH_ACCESS_TOKEN",
	OAUTH_REFRESH_TOKEN: "OAUTH_REFRESH_TOKEN",
	OAUTH_TOKEN_EXPIRATION: "OAUTH_TOKEN_EXPIRATION",
	OAUTH_REFRESH_TOKEN_EXPIRATION: "OAUTH_REFRESH_TOKEN_EXPIRATION",
	OAUTH_REFRESH_TOKEN_EXPIRATION_RESPONSE: "OAUTH_REFRESH_TOKEN_EXPIRATION_RESPONSE",
	OAUTH_REFRESH_TOKEN_EXPIRATION_DEFAULT: "OAUTH_REFRESH_TOKEN_EXPIRATION_DEFAULT",
	OAUTH_TOKEN_TYPE: "OAUTH_TOKEN_TYPE",
	OAUTH_AUTHORIZE_PARAM: "OAUTH_AUTHORIZE_PARAM",
	OAUTH_TOKEN_PARAM: "OAUTH_TOKEN_PARAM",
	OAUTH_TOKEN_EXCHANGE_GRANT_TYPE: "OAUTH_TOKEN_EXCHANGE_GRANT_TYPE",
	OAUTH_TOKEN_EXCHANGE_URL: "OAUTH_TOKEN_EXCHANGE_URL",
	OAUTH_TOKEN_EXCHANGE_TOKEN_PARAM: "OAUTH_TOKEN_EXCHANGE_TOKEN_PARAM",
	OAUTH_REFRESH_URL: "OAUTH_REFRESH_URL",
	OAUTH_REFRESH_METHOD: "OAUTH_REFRESH_METHOD",
	OAUTH_REFRESH_GRANT_TYPE: "OAUTH_REFRESH_GRANT_TYPE",
	OAUTH_REFRESH_TOKEN_PARAM: "OAUTH_REFRESH_TOKEN_PARAM",
	OAUTH_REFRESH_CREDENTIAL: "OAUTH_REFRESH_CREDENTIAL",
	OAUTH_REFRESH_SEND_CLIENT: "OAUTH_REFRESH_SEND_CLIENT",
	CONNECTION_TEST_URL: "CONNECTION_TEST_URL",
	CONNECTION_TEST_METHOD: "CONNECTION_TEST_METHOD",
	CONNECTION_TEST_AUTH: "CONNECTION_TEST_AUTH",
	CONNECTION_TEST_HEADER: "CONNECTION_TEST_HEADER",
	CONNECTION_TEST_QUERY_PARAM: "CONNECTION_TEST_QUERY_PARAM",
	CONNECTION_TEST_AUTH_SCHEME_NAME: "CONNECTION_TEST_AUTH_SCHEME_NAME",
	CONNECTION_TEST_FAIL_PATH: "CONNECTION_TEST_FAIL_PATH",
	CONNECTION_TEST_FAIL_VALUE: "CONNECTION_TEST_FAIL_VALUE",
	SOURCE_API_ROOT: "SOURCE_API_ROOT",
	CONNECTION_LOGIN_URL: "CONNECTION_LOGIN_URL",
	CONNECTION_LOGIN_BODY_TYPE: "CONNECTION_LOGIN_BODY_TYPE",
	CONNECTION_LOGIN_PARAM: "CONNECTION_LOGIN_PARAM",
	CONNECTION_LOGIN_HEADER: "CONNECTION_LOGIN_HEADER",
	CONNECTION_LOGIN_TOKEN_PATH: "CONNECTION_LOGIN_TOKEN_PATH",
	CONNECTION_LOGIN_FAIL_PATH: "CONNECTION_LOGIN_FAIL_PATH",
	CONNECTION_LOGIN_FAIL_VALUE: "CONNECTION_LOGIN_FAIL_VALUE",
	CONNECTION_LOGIN_PROBE: "CONNECTION_LOGIN_PROBE",
	AUTH_USERNAME: "AUTH_USERNAME",
	AUTH_PASSWORD: "AUTH_PASSWORD",
	AUTH_ACCESS_TOKEN: "AUTH_ACCESS_TOKEN",
	APPLICATION_ID: "APPLICATION_ID",
	CLIENT_ID: "CLIENT_ID",
	BIGQUERY_INGEST_CREDENTIALS: "BIGQUERY_INGEST_CREDENTIALS",
	BIGQUERY_INGEST_SERVICE_ACCOUNT: "BIGQUERY_INGEST_SERVICE_ACCOUNT",
} as const;

export class DatasourceService extends BaseService<IDatasourceItemModel, IDatasourceItemModel> {

	public async fetchDatasourceItems(filterData?: any): Promise<AxiosResponse<ApiResponse<Page<IDatasourceItemModel[]> & {
		counts?: {
			total?: number;
			active?: number;
			notConnected?: number;
			failed?: number;
		};
	}>>> {
		return await api.get(`v1/${this.modelUrl()}`, {
			params: filterData
		});
	}

	/** Marketplace catalog of connectable dataservices (requires Marketplace.Connect). */
	public async fetchConnectors(filterData?: any): Promise<AxiosResponse<ApiResponse<IDataserviceItemModel[]>>> {
		return await api.get(`v1/${this.modelUrl()}/connectors`, {
			params: filterData
		});
	}

	/** Marketplace connector detail including authentication types (requires Marketplace.Connect). */
	public async fetchConnector(uuid: string): Promise<AxiosResponse<ApiResponse<IDataserviceItemModel>>> {
		return await api.get(`v1/${this.modelUrl()}/connectors/${uuid}`);
	}

	public async addDatasourceItem(dataserviceUuid: string, authenticationTypeUuid?: string): Promise<AxiosResponse<ApiResponse<IDatasourceItemModel>>> {
		return await api.get(`v1/${this.modelUrl()}/new/${dataserviceUuid}`, {
			params: authenticationTypeUuid ? {authenticationTypeUuid} : undefined
		});
	}

	public async getOAuthAuthorizeUrl(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.get(`v1/${this.modelUrl()}/${datasourceUuid}/oauth/authorize-url`);
	}

	public async getConnectionStatus(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<IDatasourceOAuthStatus>>> {
		return await api.get(`v1/${this.modelUrl()}/${datasourceUuid}/connection/status`);
	}

	public async disconnectConnection(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connection/disconnect`);
	}

	public async testConnection(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<{
		healthy: boolean;
		message: string;
	}>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connection/test`);
	}

	public async requestConnectorRun(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connector/run-now`);
	}

	public async pauseConnector(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connector/pause`);
	}

	public async resumeConnector(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connector/resume`);
	}

	public async requestFullResync(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connector/full-resync`);
	}

	public async cancelPendingConnectorRun(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.post(`v1/${this.modelUrl()}/${datasourceUuid}/connector/cancel-requested-run`);
	}

	public async revealSecretFieldValue(datasourceUuid: string, authenticationTypeFieldUuid: string): Promise<AxiosResponse<ApiResponse<string>>> {
		return await api.get(`v1/${this.modelUrl()}/${datasourceUuid}/reveal-secret/${authenticationTypeFieldUuid}`);
	}

	public async fetchEvents(datasourceUuid: string): Promise<AxiosResponse<ApiResponse<IDatasourceEventModel[]>>> {
		return await api.get(`v1/${this.modelUrl()}/${datasourceUuid}/activity`);
	}

	public async fetchFieldOptions(
		datasourceUuid: string,
		fieldName: string,
	): Promise<AxiosResponse<ApiResponse<IDatasourceFieldOptionModel[]>>> {
		return await api.get(
			`v1/${this.modelUrl()}/${datasourceUuid}/field/name/${encodeURIComponent(fieldName)}/options`,
		);
	}

	protected modelUrl(): string {
		return "datasource";
	}

}